import { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ApiError,
  getUserFacingApiMessage,
  passThroughMessage,
} from "@/lib/api-client";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { pollStatus } from "@/features/identity-hub/api/identity-hub.api";
import { Sentry } from "@/lib/sentry";
import { createOrderErrorMessage } from "@/features/marketplace/lib/order-errors";
import { patchOrderAfterVideoSlotAdded } from "@/features/marketplace/lib/order-video-cache-patch";
import i18n from "@/lib/i18n";
import {
  fetchVendors,
  fetchVendorDetails,
  fetchConnections,
  createConnection,
  updateAnswers,
  resignConnection,
  disconnectVendor,
  fetchOrders,
  fetchOrder,
  fetchSentScripts,
  fetchOrderHandoffDefaults,
  previewOrderHandoffEmail,
  createOrder,
  retryOrder,
  updateOrderEta,
  updateOrderFulfillment,
  fetchAdminOrders,
  updateOrderScriptFulfillment,
  fetchPartnerOrder,
  fetchPartnerOrders,
  retryPartnerPreflightReview,
  uploadPartnerPreflight,
  addPartnerOrderScriptVideoSlot,
  deletePartnerOrderScriptVideoSlot,
  startPartnerVoiceoverCheck,
  enablePartnerLogin,
  unlinkPartnerLogin,
  createOpportunityCustomBrief,
  fetchOrderScriptPreflightComments,
  fetchPartnerOrderScriptPreflightComments,
  fetchMarketplaceOrderScriptByScriptReviewId,
  addOrderScriptPreflightComment,
  addPartnerOrderScriptNote,
  setPartnerOrderNoteReaction,
  upsertPartnerOrderThumbnail,
  deletePartnerOrderThumbnail,
} from "@/features/marketplace/api/marketplace.api";
import type {
  CreateConnectionPayload,
  UpdateAnswersPayload,
  ResignConnectionPayload,
  CreateOrderPayload,
  OrderHandoffEmailPreviewPayload,
  MarketplaceFulfillmentStatus,
  MarketplaceOrder,
  AdminOrdersFilters,
  TenantOrdersFilters,
  TenantOrdersPageResponse,
  PartnerOrder,
  PartnerOrdersFilter,
  PartnerOrdersPageResponse,
  PartnerOrdersQueryFilters,
  UpdateOrderScriptFulfillmentPayload,
  CreateCustomBriefPayload,
  MarketplaceOrderScriptByReviewLink,
  PartnerPreflightIntent,
} from "@/features/marketplace/types/marketplace.types";
import {
  fetchAdminVendors,
  fetchAdminVendor,
  createAdminVendor,
  replaceAdminVendor,
  activateAdminVendor,
  approveVendorContactEmail,
  deactivateAdminVendor,
  revokeVendorContactEmail,
  fetchPendingVendors,
  fetchPendingOrderHandoffs,
  linkPendingVendor,
  rejectPendingVendor,
  retryAdminOrderHandoff,
} from "@/features/admin/marketplace/api/admin-vendors.api";
import {
  fetchTeamRecipients,
  createTeamRecipient,
  updateTeamRecipient,
  deleteTeamRecipient,
} from "@/features/admin/marketplace/api/team-recipients.api";
import {
  acceptOrderImportWarnings,
  acceptPrefilledOrderImportDates,
  bulkSetOrderImportClientStartDates,
  bulkSetOrderImportNewVendorMapping,
  bulkSetOrderImportVendorMapping,
  commitOrderImport,
  fetchOrderImportPreview,
  parseOrderImportSpreadsheet,
  updateOrderImportRow,
} from "@/features/admin/marketplace/api/admin-order-import.api";
import type { CreativeImportPreviewDto } from "@nexus/contracts";
import { normalizeImportPreview } from "@/features/marketplace/admin/components/order-import/import-readiness";

import type { AdminVendorWritePayload } from "@/features/admin/marketplace/types/admin-marketplace.types";
import type {
  TeamRecipientCreatePayload,
  TeamRecipientUpdatePayload,
} from "@/features/admin/marketplace/types/admin-marketplace.types";
import type {
  BulkSetClientStartDateBody,
  BulkSetVendorMappingBody,
  BulkSetNewVendorMappingBody,
  UpdateCreativeImportRowBody,
} from "@nexus/contracts";
import {
  hasInFlightHandoffOrders,
  ORDER_HANDOFF_POLL_INTERVAL_MS,
} from "@/features/marketplace/lib/order-handoff-ux";
import { opportunityKeys } from "./opportunities.queries";
import { personaOpportunityScriptKeys } from "./persona-opportunity-scripts.queries";

import {
  invalidateMarketplaceOrders,
  invalidatePartnerOrderQueries,
  marketplaceKeys,
  PARTNER_ORDERS_POLL_INTERVAL_MS,
} from "./marketplace-query-keys";
import { useOrderNoteReactionMutation } from "./marketplace-reactions";

export function partnerOrdersQueryOptions(
  partnerTenantId: string,
  filters: PartnerOrdersQueryFilters = { filter: "all" },
) {
  return queryOptions({
    queryKey: marketplaceKeys.partnerOrders(partnerTenantId, filters),
    queryFn: () => fetchPartnerOrders(partnerTenantId, filters),
    staleTime: PARTNER_ORDERS_POLL_INTERVAL_MS,
    refetchInterval: PARTNER_ORDERS_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    meta: { skipGlobalError: true },
  });
}

export function usePartnerOrders(
  partnerTenantId: string | null,
  filters: PartnerOrdersQueryFilters = { filter: "all" },
) {
  return useQuery({
    ...partnerOrdersQueryOptions(partnerTenantId ?? "", filters),
    enabled: Boolean(partnerTenantId),
  });
}

const PARTNER_STORYBOARD_REFETCH_MS = 5 * 60 * 1000;

export function partnerOrderDetailQueryOptions(
  partnerTenantId: string,
  orderId: string,
) {
  return queryOptions({
    queryKey: marketplaceKeys.partnerOrder(partnerTenantId, orderId),
    queryFn: () => fetchPartnerOrder(partnerTenantId, orderId),
    staleTime: PARTNER_STORYBOARD_REFETCH_MS,
    refetchInterval: PARTNER_STORYBOARD_REFETCH_MS,
    refetchOnWindowFocus: false,
  });
}

export function usePartnerOrderDetail(
  partnerTenantId: string | null,
  orderId: string | undefined,
) {
  return useQuery({
    ...partnerOrderDetailQueryOptions(partnerTenantId ?? "", orderId ?? ""),
    enabled: Boolean(partnerTenantId && orderId),
  });
}

export function useRetryPartnerPreflightReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      partnerTenantId: string;
      orderId: string;
      orderScriptId: string;
    }) =>
      retryPartnerPreflightReview(
        args.partnerTenantId,
        args.orderId,
        args.orderScriptId,
      ),
    onSuccess: (order, args) => {
      invalidatePartnerOrderQueries(
        queryClient,
        args.partnerTenantId,
        args.orderId,
      );
      if (order.tenantId) {
        invalidateMarketplaceOrders(queryClient, order.tenantId);
      }
    },
    onError: (err, args) => {
      invalidatePartnerOrderQueries(
        queryClient,
        args.partnerTenantId,
        args.orderId,
      );
      if (useAuth.getState().activeTenantId !== args.partnerTenantId) return;
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

export function useUploadPartnerPreflight() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      partnerTenantId: string;
      orderId: string;
      orderScriptId: string;
      uploadId: string;
      intent?: PartnerPreflightIntent;
      /** Set for an alternate hook only; the primary keeps today's call. */
      hookKey?: string;
    }) =>
      uploadPartnerPreflight(
        args.partnerTenantId,
        args.orderId,
        args.orderScriptId,
        args.uploadId,
        {
          ...(args.intent ? { intent: args.intent } : {}),
          ...(args.hookKey ? { hookKey: args.hookKey } : {}),
        },
      ),
    onSuccess: (order, args) => {
      invalidatePartnerOrderQueries(
        queryClient,
        args.partnerTenantId,
        args.orderId,
      );
      if (order.tenantId) {
        invalidateMarketplaceOrders(queryClient, order.tenantId);
      }
      void queryClient.invalidateQueries({
        queryKey: marketplaceKeys.partnerPreflightComments(
          args.partnerTenantId,
          args.orderId,
          args.orderScriptId,
        ),
      });
    },
    onError: (err, args) => {
      invalidatePartnerOrderQueries(
        queryClient,
        args.partnerTenantId,
        args.orderId,
      );
      if (useAuth.getState().activeTenantId !== args.partnerTenantId) return;
      if (err instanceof ApiError && err.status === 401) return;
      const surfaced = getUserFacingApiMessage(err, passThroughMessage);
      const generic = i18n.t(
        "common.error.generic",
        "Something went wrong. Please try again.",
      );
      toast.error(
        surfaced === generic
          ? i18n.t(
              "marketplace.partner.preflightUploadError",
              "Couldn't submit creative for review",
            )
          : surfaced,
      );
    },
    meta: { skipGlobalError: true },
  });
}

export function useAddPartnerOrderScriptVideoSlot() {
  const queryClient = useQueryClient();
  const attemptKeys = useRef(new Map<string, string>());
  return useMutation({
    mutationFn: (args: {
      partnerTenantId: string;
      orderId: string;
      orderScriptId: string;
      label?: string | null;
    }) => {
      const attempt = `${args.orderId}:${args.orderScriptId}:video-slot`;
      const idempotencyKey = attemptKeys.current.get(attempt) ?? crypto.randomUUID();
      attemptKeys.current.set(attempt, idempotencyKey);
      return addPartnerOrderScriptVideoSlot(args.orderId, args.orderScriptId, {
        idempotencyKey,
        label: args.label,
      });
    },
    onSuccess: (slot, args) => {
      attemptKeys.current.delete(`${args.orderId}:${args.orderScriptId}:video-slot`);
      const patchOrder = (order: PartnerOrder) =>
        order.id === args.orderId
          ? patchOrderAfterVideoSlotAdded(order, args.orderScriptId, slot)
          : order;
      queryClient.setQueriesData<PartnerOrdersPageResponse>(
        { queryKey: marketplaceKeys.partnerOrdersRoot(args.partnerTenantId) },
        (current) => {
          if (!current?.orders) return current;
          return {
            ...current,
            orders: current.orders.map(patchOrder),
          };
        },
      );
      queryClient.setQueryData<PartnerOrder>(
        marketplaceKeys.partnerOrder(args.partnerTenantId, args.orderId),
        (current) => (current ? patchOrder(current) : current),
      );
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error(
          i18n.t(
            "marketplace.partner.videos.slotInProgress",
            "A video slot is already being added. Wait a moment and try again.",
          ),
        );
        return;
      }
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

export function useDeletePartnerOrderScriptVideoSlot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      partnerTenantId: string;
      orderId: string;
      orderScriptId: string;
      deliverableId: string;
    }) =>
      deletePartnerOrderScriptVideoSlot(
        args.orderId,
        args.orderScriptId,
        args.deliverableId,
      ),
    onSuccess: (order, args) => {
      queryClient.setQueriesData<PartnerOrdersPageResponse>(
        { queryKey: marketplaceKeys.partnerOrdersRoot(args.partnerTenantId) },
        (current) => {
          if (!current?.orders) return current;
          return {
            ...current,
            orders: current.orders.map((row) =>
              row.id === order.id ? order : row,
            ),
          };
        },
      );
      queryClient.setQueryData<PartnerOrder>(
        marketplaceKeys.partnerOrder(args.partnerTenantId, args.orderId),
        order,
      );
      toast.success(
        i18n.t(
          "marketplace.partner.videos.emptySlotRemoved",
          "Empty video slot removed",
        ),
      );
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

/**
 * Transcription takes ~30s on the worker's shared Gemini lane, so allow ~90s
 * before giving up. Giving up only costs the advisory warning.
 */
const VOICEOVER_CHECK_POLL_ATTEMPTS = 25;
const VOICEOVER_CHECK_POLL_INTERVAL_MS = 500;

/**
 * Starts the pre-submit voiceover check and polls for the score. Resolves to
 * the mismatch (0–1), or null when the order line has no readable voiceover.
 *
 * Deliberately not useAmberMutation: that toasts on success and error, and this answer is
 * advisory — a dead check must stay silent and never block submit.
 */
export function usePartnerVoiceoverCheck() {
  return useMutation({
    mutationFn: async (args: {
      partnerTenantId: string;
      orderId: string;
      orderScriptId: string;
      uploadId: string;
      hookKey?: string;
    }): Promise<number | null> => {
      const start = await startPartnerVoiceoverCheck(
        args.partnerTenantId,
        args.orderId,
        args.orderScriptId,
        args.uploadId,
        args.hookKey,
      );
      if (!start.checkable) return null;
      const status = await pollStatus(
        "/marketplace/partner",
        start.correlationId,
        VOICEOVER_CHECK_POLL_ATTEMPTS,
        VOICEOVER_CHECK_POLL_INTERVAL_MS,
      );
      if (status.status === "FAILED")
        throw new Error("The voiceover check failed");
      return typeof status.mismatch === "number" ? status.mismatch : null;
    },
    meta: { skipGlobalError: true },
  });
}

export function useEnablePartnerLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vendorId: string) => enablePartnerLogin(vendorId),
    onSuccess: (data, vendorId) => {
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminVendor(vendorId),
      });
      queryClient.invalidateQueries({ queryKey: marketplaceKeys.adminVendors });
      toast.success(
        data.alreadyEnabled
          ? i18n.t(
              "marketplace.admin.partner.alreadyConnected",
              "Partner org already linked",
            )
          : i18n.t(
              "marketplace.admin.partner.connected",
              "Partner org connected",
            ),
      );
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

export function useUnlinkPartnerLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vendorId: string) => unlinkPartnerLogin(vendorId),
    onSuccess: (_data, vendorId) => {
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminVendor(vendorId),
      });
      queryClient.invalidateQueries({ queryKey: marketplaceKeys.adminVendors });
      toast.success(
        i18n.t("marketplace.admin.partner.unlinked", "Partner org unlinked"),
      );
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

export function partnerOrderScriptPreflightCommentsQueryOptions(
  partnerTenantId: string,
  orderId: string,
  orderScriptId: string,
  deliverableId?: string,
) {
  return queryOptions({
    queryKey: marketplaceKeys.partnerPreflightComments(
      partnerTenantId,
      orderId,
      orderScriptId,
      deliverableId,
    ),
    queryFn: () =>
      fetchPartnerOrderScriptPreflightComments(
        partnerTenantId,
        orderId,
        orderScriptId,
        deliverableId,
      ),
  });
}

export function usePartnerOrderScriptPreflightComments(
  partnerTenantId: string | null,
  orderId: string,
  orderScriptId: string,
  enabled = true,
  deliverableId?: string,
) {
  return useQuery({
    ...partnerOrderScriptPreflightCommentsQueryOptions(
      partnerTenantId ?? "",
      orderId,
      orderScriptId,
      deliverableId,
    ),
    enabled: Boolean(partnerTenantId) && enabled,
  });
}

export function useSetPartnerOrderNoteReaction() {
  return useOrderNoteReactionMutation("partner", setPartnerOrderNoteReaction);
}

export function useAddPartnerOrderScriptNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      partnerTenantId: string;
      orderId: string;
      orderScriptId: string;
      body: string;
      deliverableId?: string;
    }) =>
      addPartnerOrderScriptNote(
        args.partnerTenantId,
        args.orderId,
        args.orderScriptId,
        {
          body: args.body,
          ...(args.deliverableId ? { deliverableId: args.deliverableId } : {}),
        },
      ),
    onSuccess: (_data, args) => {
      void queryClient.invalidateQueries({
        queryKey: marketplaceKeys.partnerPreflightComments(
          args.partnerTenantId,
          args.orderId,
          args.orderScriptId,
        ),
      });
      invalidatePartnerOrderQueries(
        queryClient,
        args.partnerTenantId,
        args.orderId,
      );
      if (useAuth.getState().activeTenantId === args.partnerTenantId) {
        toast.success(
          i18n.t("marketplace.orders.activity.commentSent", "Comment sent"),
        );
      }
    },
    onError: (_error, args) => {
      if (useAuth.getState().activeTenantId === args.partnerTenantId) {
        toast.error(
          i18n.t(
            "marketplace.orders.preflightCommentError",
            "Couldn't send comment",
          ),
        );
      }
    },
    meta: { skipGlobalError: true },
  });
}

export function useUpsertPartnerOrderThumbnail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: upsertPartnerOrderThumbnail,
    onSuccess: (order, args) => {
      invalidatePartnerOrderQueries(
        queryClient,
        args.partnerTenantId,
        args.orderId,
      );
      if (order.tenantId) {
        invalidateMarketplaceOrders(queryClient, order.tenantId);
      }
    },
  });
}

export function useDeletePartnerOrderThumbnail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePartnerOrderThumbnail,
    onSuccess: (order, args) => {
      invalidatePartnerOrderQueries(
        queryClient,
        args.partnerTenantId,
        args.orderId,
      );
      if (order.tenantId) {
        invalidateMarketplaceOrders(queryClient, order.tenantId);
      }
    },
  });
}
